import React, { useState, useEffect } from "react";
import { Navbar, Nav } from "react-bootstrap";
import { Link } from "react-router-dom";
import TagManager from "react-gtm-module";
import {
  kirkos_logo_white_yellow,
  icon_normal,
  icon_black,
} from "../../assets/images";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Customer/homepage/styles.css";
import "./ContactUs.css";
import SEO from "../../components/SEO";


/* ─── contact details ─────────────────────────────────────────────── */
const WHATSAPP_NUMBER = "+97165674835";
const PHONE_NUMBER = "+97165699535";
const EMAIL = "support@kirkos.com";

/* ─── UAE business-hours helper (UTC+4, no DST) ───────────────────── */
const isUAEBusinessHours = (): boolean => {
  const nowUTC = new Date();
  const nowUAE = new Date(nowUTC.getTime() + 4 * 60 * 60 * 1000);
  const day = nowUAE.getUTCDay();              // 0=Sun…6=Sat
  const mins = nowUAE.getUTCHours() * 60 + nowUAE.getUTCMinutes();
  return day >= 1 && day <= 6 && mins >= 540 && mins < 1080; // Mon-Sat, 09:00-18:00
};

const useBusinessHoursStatus = () => {
  const [isOnline, setIsOnline] = useState(isUAEBusinessHours);
  useEffect(() => {
    const id = setInterval(() => setIsOnline(isUAEBusinessHours()), 60_000);
    return () => clearInterval(id);
  }, []);
  return isOnline;
};

/* ─── WhatsApp smart-redirect (same logic as sidebar) ────────────── */
const handleWhatsAppClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  const cleanNumber = WHATSAPP_NUMBER.replace(/\+/g, "");
  const mobileLink = `https://wa.me/${cleanNumber}`;
  const webLink = `https://web.whatsapp.com/send?phone=${cleanNumber}`;
  const isMob = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const focusTab = () => {
    const tab = window.open("", "whatsapp");
    if (tab && !tab.closed && tab.location.href.includes("web.whatsapp.com")) {
      tab.focus(); return true;
    }
    return false;
  };

  const tryDeepLink = (deepLink: string, fallback: () => void, delay: number) => {
    let iframe: HTMLIFrameElement | null = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe && document.body.contains(iframe)) document.body.removeChild(iframe);
      iframe = null;
    };
    const onBlur = () => { clearTimeout(timer); cleanup(); window.removeEventListener("blur", onBlur); window.removeEventListener("focus", onFocus); };
    const onFocus = () => setTimeout(() => { cleanup(); fallback(); }, 500);
    const timer = setTimeout(() => { cleanup(); fallback(); }, delay);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    iframe.src = deepLink;
  };

  if (isMob) {
    // Universal link is the most reliable way on iOS/Android
    window.location.href = mobileLink;
  } else {
    // Desktop: try deep link app first, fallback to web
    tryDeepLink(`whatsapp://send?phone=${cleanNumber}`, () => { if (!focusTab()) window.open(webLink, "whatsapp"); }, 1500);
  }
};

/* ─── Component ───────────────────────────────────────────────────── */
const PublicContactUs: React.FC = () => {
  const isOnline = useBusinessHoursStatus();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="pb-4">
      <SEO
        title="Contact Us | Kirkos Customer Support Dubai"
        description="Get in touch with Kirkos for premium car rental support in Dubai. Reach us via WhatsApp, phone, or email for any assistance."
        keywords="contact kirkos, dubai car rental support, kirkos uae contact, car hire assistance"
      />

      {/* ── Page body ─────────────────────────────────────────────── */}
      <div className="pcu-body">
        {/* Ambient elements */}
        <div className="pcu-ambient pcu-ambient--1"></div>
        <div className="pcu-ambient pcu-ambient--2"></div>

        <div className="pcu-container">

          {/* Header */}
          <div className="pcu-header">
            <div className="pcu-icon-wrap">
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
                <path d="M21 2H3C2.45 2 2 2.45 2 3V17C2 17.55 2.45 18 3 18H6V22.5L10.5 18H21C21.55 18 22 17.55 22 17V3C22 2.45 21.55 2 21 2ZM20 16H10L7 18.5V16H4V4H20V16Z" fill="#FFD700" />
              </svg>
            </div>
            <h1 className="pcu-title">Contact Us</h1>
            <p className="pcu-subtitle">
              We're here to help. Reach out through any of the channels below.
            </p>
          </div>

          {/* Scrollable View Area */}
          <div className="pcu-scroll-view">

            {/* Status Hero */}
            <div className={`pcu-status ${isOnline ? "pcu-status--online" : "pcu-status--offline"}`}>
              <div className="pcu-status-left">
                <span className={`pcu-dot ${isOnline ? "pcu-dot--online" : "pcu-dot--offline"}`} />
                <div className="pcu-status-text">
                  {isOnline ? (
                    <>
                      <span className="pcu-status-label">We're Online</span>
                      <span className="pcu-status-sub">Our team is available now — expect a quick response!</span>
                    </>
                  ) : (
                    <>
                      <span className="pcu-status-label">Currently Offline</span>
                      <span className="pcu-status-sub">Available <strong>Mon – Sat, 9:00 AM – 6:00 PM</strong> UAE time</span>
                    </>
                  )}
                </div>
              </div>
              <div className="pcu-status-badge">{isOnline ? "LIVE" : "CLOSED"}</div>
            </div>

            {/* Offline notice */}
            {!isOnline && (
              <div className="pcu-notice">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10" stroke="#f0a500" strokeWidth="2" />
                  <path d="M12 8v4M12 16h.01" stroke="#f0a500" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>
                  Thank you for contacting <strong>KIRKOS</strong>. Your message is important to us
                  and we will get back to you promptly during working hours.
                </span>
              </div>
            )}

            {/* Contact cards */}
            <div className="pcu-cards">

              {/* Email */}
              <a
                href={`mailto:${EMAIL}`}
                className="pcu-card"
                id="pcu-email"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: "support_chat_open",
                      page_type: "contact_us",
                      page_path: window.location.pathname,
                      page_url: window.location.href,
                      contact_method: "email",
                    }
                  });
                }}
              >
                <div className="pcu-card-icon pcu-email-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="pcu-card-content">
                  <span className="pcu-card-label">Email Support</span>
                  <span className="pcu-card-value">{EMAIL}</span>
                </div>
                <div className="pcu-card-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </a>

              {/* Phone */}
              <a
                href={`tel:${PHONE_NUMBER}`}
                className="pcu-card"
                id="pcu-phone"
                onClick={() => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: "call_click",
                      page_type: "contact_us",
                      page_path: window.location.pathname,
                      page_url: window.location.href,
                      phone_number: PHONE_NUMBER,
                      contact_method: "phone",
                    }
                  });
                }}
              >
                <div className="pcu-card-icon pcu-phone-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <path d="M6.62 10.79C8.06 13.62 10.38 15.93 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="pcu-card-content">
                  <span className="pcu-card-label">Phone Number</span>
                  <span className="pcu-card-value">{PHONE_NUMBER}</span>
                </div>
                <div className="pcu-card-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </a>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: "support_chat_open",
                      page_type: "contact_us",
                      page_path: window.location.pathname,
                      page_url: window.location.href,
                      phone_number: WHATSAPP_NUMBER,
                      contact_method: "whatsapp",
                    }
                  });
                  handleWhatsAppClick(e);
                }}
                className="pcu-card pcu-wa-card"
                id="pcu-whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="pcu-card-icon pcu-wa-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <path d="M17.472 14.382C17.1 14.196 15.281 13.302 14.943 13.18C14.605 13.057 14.358 12.997 14.111 13.369C13.864 13.741 13.148 14.574 12.931 14.822C12.715 15.069 12.498 15.099 12.126 14.913C11.754 14.726 10.566 14.341 9.162 13.082C8.065 12.101 7.316 10.893 7.099 10.521C6.883 10.149 7.077 9.949 7.263 9.764C7.43 9.597 7.635 9.33 7.821 9.114C8.007 8.897 8.067 8.741 8.189 8.494C8.311 8.247 8.251 8.031 8.159 7.845C8.067 7.659 7.321 5.837 7.013 5.09C6.713 4.363 6.409 4.461 6.184 4.449C5.967 4.438 5.72 4.436 5.473 4.436C5.226 4.436 4.823 4.528 4.485 4.9C4.147 5.272 3.185 6.166 3.185 7.988C3.185 9.81 4.515 11.572 4.701 11.819C4.887 12.066 7.308 15.814 11.034 17.424C11.921 17.808 12.616 18.038 13.156 18.208C14.046 18.489 14.858 18.449 15.5 18.354C16.216 18.248 17.7 17.462 18.009 16.598C18.318 15.734 18.318 14.988 18.226 14.822C18.134 14.656 17.887 14.565 17.472 14.382ZM10.52 21.335H10.514C8.925 21.334 7.366 20.903 6.007 20.089L5.677 19.895L2.456 20.74L3.316 17.601L3.104 17.259C2.211 15.852 1.739 14.215 1.74 12.534C1.742 7.53 5.783 3.49 10.524 3.49C12.821 3.491 14.979 4.386 16.601 6.01C18.222 7.633 19.115 9.793 19.114 12.09C19.112 17.095 15.072 21.335 10.52 21.335ZM20.007 3.487C17.894 1.372 15.09 0.201 12.099 0.2C5.93 0.2 0.907 5.221 0.905 11.393C0.904 13.362 1.422 15.284 2.406 16.977L0.8 23L6.975 21.426C8.607 22.321 10.435 22.793 12.295 22.794H12.3C18.469 22.794 23.493 17.773 23.495 11.601C23.496 8.614 22.327 5.807 20.007 3.487Z" fill="currentColor" />
                  </svg>
                </div>
                <div className="pcu-card-content">
                  <span className="pcu-card-label">WhatsApp</span>
                  <span className="pcu-card-value">{WHATSAPP_NUMBER}</span>
                </div>
                <div className="pcu-card-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </a>

            </div>

            {/* Footer home link */}
            {/* <div className="pcu-footer-link">
              <Link to="/" className="pcu-back-home">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6 }}>
                  <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back to Home
              </Link>
            </div> */}
          </div>

        </div>
      </div>
    </div>
  );
};

export default PublicContactUs;
